import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { RewardChip } from '@/features/guide-rewards/components/RewardChip';
import { adminGrowthApi, adminGrowthKeys, type GuideSummary } from '../api/admin-growth.api';
import { FilterBar, GrowthTable, fmtDate, fmtNum, humanize } from './GrowthShared';

function Person({ person }: { person: Partial<GuideSummary> & { name: string } | null }) {
  if (!person) return <>—</>;
  return (
    <>
      <Typography fontWeight={600} fontSize="0.875rem">{person.name}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: person.guideId ? 'ui-monospace, monospace' : undefined }}>
        {person.guideId ?? person.email ?? ''}
      </Typography>
    </>
  );
}

export function GrowthReferralsPanel() {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const query = {
    page: page + 1,
    limit,
    search: search.trim() || undefined,
    status: status || undefined,
    from: from || undefined,
    to: to ? dayjs(to).endOf('day').toISOString() : undefined,
  };
  const { data, isLoading, isError } = useQuery({
    queryKey: adminGrowthKeys.referrals(query),
    queryFn: () => adminGrowthApi.referrals(query),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <FilterBar>
        <TextField size="small" label="Search guide, email, Guide ID" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} sx={{ minWidth: 260 }} />
        <TextField size="small" select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
          <MenuItem value="">All</MenuItem>
          {['REGISTERED', 'ONBOARDING', 'APPROVED', 'REJECTED'].map((s) => <MenuItem key={s} value={s}>{humanize(s)}</MenuItem>)}
        </TextField>
        <TextField size="small" type="date" label="From" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="To" value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} />
      </FilterBar>
      <GrowthTable
        loading={isLoading}
        error={isError}
        rows={data?.items}
        rowKey={(r) => r.id}
        emptyText="No referrals match these filters."
        page={page}
        limit={limit}
        total={data?.total}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(0); }}
        columns={[
          { key: 'referrer', label: 'Referrer', render: (r) => <Person person={r.referrer} /> },
          { key: 'referred', label: 'Referred guide', render: (r) => <Person person={r.referred} /> },
          { key: 'source', label: 'Source', render: (r) => (r.source === 'LINK' ? 'Referral link' : 'Typed Guide ID') },
          { key: 'created', label: 'Applied', render: (r) => fmtDate(r.createdAt) },
          { key: 'onboarded', label: 'Onboarded', render: (r) => fmtDate(r.onboardingCompletedAt) },
          { key: 'status', label: 'Status', render: (r) => <RewardChip label={humanize(r.status)} status={r.status} /> },
          {
            key: 'reward',
            label: 'Reward',
            align: 'right',
            render: (r) => (r.reward ? <><RewardChip label={humanize(r.reward.status)} status={r.reward.status} /> <Typography component="span" fontWeight={700} fontSize="0.85rem">{fmtNum(r.reward.credits)}</Typography></> : '—'),
          },
        ]}
      />
    </>
  );
}
